import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, Check, GitBranch, ScanSearch, Loader2, X, Sparkles, Database, ExternalLink } from "lucide-react";
import { Panel, Pill } from "./ui";
import { nexsysApi } from "./api";
import { ReasoningStream } from "../components/ReasoningStream";

const PRIORITY = {
  high: { tone: "magenta", label: "HIGH PRIORITY", bar: "#ec4899" },
  medium: { tone: "amber", label: "MEDIUM", bar: "#f59e0b" },
  low: { tone: "indigo", label: "LOW", bar: "#818cf8" },
};
const TONE = { emerald: "#34d399", cyan: "#22d3ee", indigo: "#818cf8", amber: "#f59e0b", magenta: "#ec4899" };

function md(text) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc(text || "")
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/^\s*[-•]\s?(.*)$/gm, "<span class='block pl-3 -indent-3'>• $1</span>")
    .replace(/\n{2,}/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");
}

function ResultModal({ state, onClose }) {
  const open = !!state;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[105] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={onClose} data-testid="decision-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="nx-panel nx-corners flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-indigo-500">
                  {state?.kind === "sim" ? <GitBranch className="h-4 w-4 text-black" /> : <ScanSearch className="h-4 w-4 text-black" />}
                </span>
                <div>
                  <h3 className="font-display text-sm font-semibold text-white">{state?.kind === "sim" ? "Alternative Options" : "Supporting Analysis"}</h3>
                  <p className="font-mono text-[10px] tracking-wider text-white/40">{state?.subtitle}</p>
                </div>
              </div>
              <button onClick={onClose} className="rounded-lg p-1.5 text-white/50 hover:bg-white/10 hover:text-white" data-testid="decision-modal-close"><X className="h-5 w-5" /></button>
            </div>

            <div className="nx-scroll overflow-y-auto p-5">
              {state?.loading && (
                <div className="py-6">
                  <ReasoningStream preset={state.kind === "sim" ? "simulate" : "analysis"} />
                </div>
              )}

              {!state?.loading && state?.kind === "sim" && (
                <div className="space-y-3">
                  {(state.data || []).map((a, i) => (
                    <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="decision-alternative">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-display text-sm font-semibold text-white/90">{a.title}</h4>
                        <Pill tone="cyan" dot={false}>{a.confidence}%</Pill>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(a.impact || []).map((m, j) => (
                          <span key={j} className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-1 text-[11px] text-white/70">
                            <span className="text-white/40">{m.label}: </span><span className="font-medium text-cyan-200">{m.value}</span>
                          </span>
                        ))}
                      </div>
                      {a.tradeoff && <p className="mt-3 text-[12px] leading-relaxed text-white/50"><span className="text-amber-300/80">Trade-off: </span>{a.tradeoff}</p>}
                    </div>
                  ))}
                  {(!state.data || state.data.length === 0) && <p className="text-sm text-white/50">No alternatives returned. Try again.</p>}
                </div>
              )}

              {!state?.loading && state?.kind === "analysis" && (
                <div className="text-[13px] leading-relaxed text-white/75" dangerouslySetInnerHTML={{ __html: md(state.data || "") }} />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DecisionCard({ d, index, onGoverned, registered }) {
  const [approved, setApproved] = useState(false);
  const [approving, setApproving] = useState(false);
  const [govInfo, setGovInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [modal, setModal] = useState(null);
  useEffect(() => { if (registered) setApproved(true); }, [registered]);
  const p = PRIORITY[d.priority] || PRIORITY.low;
  const payload = { id: d.id, title: d.title, program: d.program, rationale: d.rationale };

  const approve = async () => {
    if (approved || approving) return;
    setApproving(true);
    try {
      const r = await nexsysApi.approveDecision(payload);
      setApproved(true);
      setGovInfo(r?.governed || null);
      if (r?.governed?.registered && typeof onGoverned === "function") onGoverned();
    } catch {
      setGovInfo({ registered: false, error: true,
        reason: "approval not recorded — not authorized (read-only access) or request failed" });
    }
    finally { setApproving(false); }
  };
  const simulate = async () => {
    setBusy("sim");
    setModal({ kind: "sim", loading: true, subtitle: d.program });
    try { const r = await nexsysApi.simulateDecision(payload); setModal({ kind: "sim", loading: false, subtitle: d.program, data: r.alternatives }); }
    catch { setModal({ kind: "sim", loading: false, subtitle: d.program, data: [] }); }
    finally { setBusy(""); }
  };
  const analyze = async () => {
    setBusy("analysis");
    setModal({ kind: "analysis", loading: true, subtitle: d.program });
    try { const r = await nexsysApi.analyzeDecision(payload); setModal({ kind: "analysis", loading: false, subtitle: d.program, data: r.analysis }); }
    catch { setModal({ kind: "analysis", loading: false, subtitle: d.program, data: "Analysis unavailable. Please try again." }); }
    finally { setBusy(""); }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
        className="nx-panel nx-panel-hover nx-corners relative overflow-hidden"
        data-testid="decision-card"
      >
        <span className="absolute inset-y-0 left-0 w-1" style={{ background: p.bar, boxShadow: `0 0 18px ${p.bar}` }} />
        <div className="p-5 pl-6">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={p.tone} dot={d.priority === "high"}>{p.label}</Pill>
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">{d.program}</span>
          </div>

          <div className="mt-3 flex items-start gap-2.5">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400/20 to-indigo-500/20">
              <Lightbulb className="h-4 w-4 text-cyan-300" />
            </span>
            <h3 className="font-display text-base font-semibold leading-snug text-white">{d.title}</h3>
          </div>
          <p className="mt-2.5 text-[13px] leading-relaxed text-white/55">{d.rationale}</p>

          {/* impact */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            {d.impact.map((m, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="font-mono text-[9px] uppercase tracking-wider text-white/40">{m.label}</div>
                <div className="mt-1 font-display text-sm font-bold" style={{ color: TONE[m.tone] || "#22d3ee" }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* confidence */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-mono uppercase tracking-wider text-white/40">Confidence</span>
              <span className="font-display font-bold text-cyan-300">{d.confidence}%</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
              <motion.div
                initial={{ width: 0 }} animate={{ width: `${d.confidence}%` }} transition={{ duration: 1, delay: index * 0.06 + 0.2 }}
                className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-indigo-500"
              />
            </div>
          </div>

          {/* actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={approve}
              disabled={approving}
              className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors ${
                approved ? "cursor-default bg-emerald-500/20 text-emerald-300" : "bg-cyan-500 text-black hover:bg-cyan-400"
              }`}
              data-testid="decision-approve"
            >
              {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {approved ? "Approved" : "Approve"}
            </button>
            <button onClick={simulate} disabled={!!busy} className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-[13px] text-white/80 transition-colors hover:border-cyan-400/40 disabled:opacity-50" data-testid="decision-simulate">
              {busy === "sim" ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitBranch className="h-4 w-4 text-cyan-300" />}
              Simulate Alternatives
            </button>
            <button onClick={analyze} disabled={!!busy} className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2 text-[13px] text-white/80 transition-colors hover:border-cyan-400/40 disabled:opacity-50" data-testid="decision-analysis">
              {busy === "analysis" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4 text-cyan-300" />}
              View Supporting Analysis
            </button>
          </div>

          {approved && govInfo && (
            <div className={`mt-3 rounded-lg border px-3 py-2 text-[11px] ${govInfo.registered ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-white/15 bg-white/[0.04] text-white/55"}`} data-testid="decision-governed-status">
              {govInfo.registered
                ? <>Governed Decision Object registered{govInfo.already_registered ? " (existing)" : ""} · id <span className="font-mono">{govInfo.governed_object_id}</span>{govInfo.source_verified === false && <span className="text-amber-300"> · source not re-verifiable at approval (flagged)</span>}<br />Execution requires separate authorization — use "Authorizable Decisions" on the Execution Console to authorize, then execute.</>
                : <>Logged only — {govInfo.reason}</>}
            </div>
          )}
          {!approved && govInfo?.error && (
            <div className="mt-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[11px] text-red-300" data-testid="decision-approve-error">{govInfo.reason}</div>
          )}
        </div>
      </motion.div>

      <ResultModal state={modal} onClose={() => setModal(null)} />
    </>
  );
}

function GovernedExecutionPanel() {
  const [create, setCreate] = useState({ parent_decision_id: "live-HYP-132", parent_version: "1", title: "" });
  const [attach, setAttach] = useState({ decision_id: "", kind: "jira.transition", issue_key: "HYP-132", to_status: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState("");
  const say = (ok, text) => setMsg({ ok, text });
  const fail = (e) => say(false, e?.detail || e?.message || "request refused");

  const doCreate = async () => {
    setBusy("create");
    try {
      const r = await nexsysApi.createExecutionDecision({
        parent_decision_id: create.parent_decision_id.trim(),
        parent_version: parseInt(create.parent_version, 10) || 1,
        title: create.title.trim(),
      });
      setAttach((a) => ({ ...a, decision_id: r.decision.id }));
      say(true, `Execution decision ${r.decision.id} created (proposed, lineage ${r.decision.lineage.parent_decision_id} v${r.decision.lineage.parent_version}). Approve it via the lifecycle, attach the effect, then authorize + execute.`);
    } catch (e) { fail(e); } finally { setBusy(""); }
  };
  const doAttach = async () => {
    setBusy("attach");
    try {
      const r = await nexsysApi.attachExecutionEffect(attach.decision_id.trim(), {
        kind: attach.kind.trim(), issue_key: attach.issue_key.trim(), to_status: attach.to_status.trim(),
      });
      say(true, `Effect attached — ${r.decision.execution_effect.issue_key} → "${r.decision.execution_effect.to_status}" · version ${r.decision.version} · hash ${r.effect_hash.slice(0, 12)}…`);
    } catch (e) { fail(e); } finally { setBusy(""); }
  };

  const inp = "w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-[12px] text-white/85 outline-none focus:border-cyan-400/50";
  return (
    <Panel title="Governed Execution" subtitle="OWNER-ONLY · EXECUTION DECISION WITH IMMUTABLE LINEAGE · ONE EFFECT, ONE ISSUE" data-testid="governed-execution-panel">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4" data-testid="exec-decision-create">
          <h4 className="font-display text-[13px] font-semibold text-white/90">1 · Create execution decision</h4>
          <p className="mt-1 text-[11px] text-white/45">References the parent approval by id + version. The parent is never touched.</p>
          <div className="mt-3 space-y-2">
            <input className={inp} value={create.parent_decision_id} onChange={(e) => setCreate({ ...create, parent_decision_id: e.target.value })} placeholder="parent decision id" data-testid="exec-parent-id-input" />
            <input className={inp} value={create.parent_version} onChange={(e) => setCreate({ ...create, parent_version: e.target.value })} placeholder="parent version" data-testid="exec-parent-version-input" />
            <input className={inp} value={create.title} onChange={(e) => setCreate({ ...create, title: e.target.value })} placeholder="title (optional)" data-testid="exec-title-input" />
            <button onClick={doCreate} disabled={busy === "create"} className="rounded-xl bg-cyan-500 px-4 py-2 text-[12px] font-semibold text-black hover:bg-cyan-400 disabled:opacity-50" data-testid="exec-decision-create-btn">
              {busy === "create" ? "Creating…" : "Create execution decision"}
            </button>
          </div>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4" data-testid="exec-effect-attach">
          <h4 className="font-display text-[13px] font-semibold text-white/90">2 · Attach Jira effect (append-only, audited)</h4>
          <p className="mt-1 text-[11px] text-white/45">Creates a new decision version. Refused on invalid lineage, unsupported kind, or an already-APPLIED effect.</p>
          <div className="mt-3 space-y-2">
            <input className={inp} value={attach.decision_id} onChange={(e) => setAttach({ ...attach, decision_id: e.target.value })} placeholder="execution decision id" data-testid="attach-decision-id-input" />
            <input className={inp} value={attach.issue_key} onChange={(e) => setAttach({ ...attach, issue_key: e.target.value })} placeholder="issue key (e.g. HYP-132)" data-testid="attach-issue-key-input" />
            <input className={inp} value={attach.to_status} onChange={(e) => setAttach({ ...attach, to_status: e.target.value })} placeholder="target status (owner-specified)" data-testid="attach-to-status-input" />
            <button onClick={doAttach} disabled={busy === "attach"} className="rounded-xl bg-indigo-500 px-4 py-2 text-[12px] font-semibold text-white hover:bg-indigo-400 disabled:opacity-50" data-testid="exec-effect-attach-btn">
              {busy === "attach" ? "Attaching…" : "Attach effect"}
            </button>
          </div>
        </div>
      </div>
      {msg && (
        <div className={`mt-3 rounded-lg border px-3 py-2 text-[11px] ${msg.ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-red-400/30 bg-red-500/10 text-red-300"}`} data-testid="governed-execution-msg">
          {msg.text}
        </div>
      )}
    </Panel>
  );
}

export function DecisionCenter() {
  const [items, setItems] = useState(null);
  const [source, setSource] = useState(null);
  const [governed, setGoverned] = useState([]);
  const [legacyCount, setLegacyCount] = useState(0);
  const loadGoverned = () =>
    nexsysApi.memory(6).then((d) => { setGoverned(d.memory || []); setLegacyCount(d.legacy_pre_integration_count || 0); }).catch(() => {});
  useEffect(() => {
    nexsysApi.decisions()
      .then((d) => { const liveSrc = d.source && d.source !== "synthetic"; setItems(liveSrc ? (d.decisions || []) : []); setSource(d.source || "synthetic"); })
      .catch(() => { setItems([]); setSource("synthetic"); });
    loadGoverned();
  }, []);
  const isLive = source && source !== "synthetic";

  const openInCognitive = async (id) => {
    try {
      const d = await nexsysApi.getDecision(id);
      window.__nexsysPendingDecision = d;
      if (typeof window.__nexsysSetView === "function") window.__nexsysSetView("cognitive");
      setTimeout(() => { if (typeof window.__nexsysShowDecision === "function") window.__nexsysShowDecision(d); }, 80);
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-5" data-testid="decision-center">
      <Panel
        title="Decision Center"
        subtitle={isLive ? "AI-RECOMMENDED ACTIONS · GROUNDED IN LIVE PORTFOLIO DATA" : "AI-RECOMMENDED ACTIONS · SAMPLE DATA (NO LIVE CONNECTOR)"}
        right={<Pill tone="cyan"><Sparkles className="h-3 w-3" /> {items ? `${items.length} recommendations` : "…"}</Pill>}
      >
        <p className="text-[13px] leading-relaxed text-white/55">
          NexSys continuously analyzes your portfolio and proposes the highest-leverage next moves —
          each with projected impact and a confidence score. Approve to log the decision, simulate
          alternatives, or open the supporting analysis. Ask Chatty to "open Decision Center" any time.
        </p>
      </Panel>

      {(governed.length > 0 || legacyCount > 0) && (
        <Panel
          title="Governed Decision Objects"
          subtitle="OPERATIONAL REGISTRY · LIVE SOURCE LINEAGE · FULL EXPLAINABILITY"
          right={<Pill tone="indigo"><Database className="h-3 w-3" /> {governed.length}</Pill>}
        >
          {governed.length === 0 && (
            <p className="text-[12px] text-white/45" data-testid="governed-empty">No operational governed decision objects yet — approve a live-sourced decision above to register one.</p>
          )}
          <div className="space-y-2" data-testid="governed-decisions">
            {governed.map((g) => {
              const c = { approved: "#34d399", rejected: "#ef4444", deferred: "#f59e0b", proposed: "#94a3b8" }[g.status] || "#94a3b8";
              return (
                <div key={g.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="governed-row">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: c, boxShadow: `0 0 8px ${c}` }} />
                  <span className="font-display text-[13px] font-semibold text-white/90">{g.title}</span>
                  <span className="min-w-0 flex-1 truncate text-[12px] text-white/45">{g.summary}</span>
                  {g.decision?.confidence != null && <span className="font-mono text-[11px] text-cyan-300">{g.decision.confidence}%</span>}
                  <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: c }}>{g.status}</span>
                  <button onClick={() => openInCognitive(g.id)} className="flex items-center gap-1 rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-[11px] font-medium text-cyan-200 hover:bg-cyan-400/20" data-testid={`open-governed-${g.id}`}>
                    <ExternalLink className="h-3 w-3" /> Open
                  </button>
                </div>
              );
            })}
          </div>
          {legacyCount > 0 && (
            <p className="mt-3 text-[11px] text-white/35" data-testid="governed-legacy-note">
              {legacyCount} legacy pre-integration demonstration record{legacyCount === 1 ? "" : "s"} excluded from the operational registry (preserved — removable only via the governed retirement lifecycle).
            </p>
          )}
        </Panel>
      )}

      {localStorage.getItem("nexsys_gate_role") === "super_admin" && <GovernedExecutionPanel />}

      {!items && (
        <div className="flex items-center gap-3 py-10 text-white/50"><Loader2 className="h-5 w-5 animate-spin text-cyan-300" /> Loading recommendations…</div>
      )}

      {items && items.length === 0 && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-8 text-center text-sm text-white/45">
          No open decision points from your connected data right now.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {(items || []).map((d, i) => (
          <DecisionCard key={d.id} d={d} index={i} onGoverned={loadGoverned}
            registered={governed.some((g) => g.id === d.id)} />
        ))}
      </div>
    </div>
  );
}
